import { GoogleMapsOverlay as DeckOverlay } from '@deck.gl/google-maps';
import { IconLayer } from '@deck.gl/layers';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Injectable, OnDestroy } from '@angular/core';

import { SnAbilityService } from '@lib/ability';
import { SnSessionService } from '@lib/auth';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { GoogleMap, GoogleMapLayer } from '@lib/geo/google/map';
import { FsPointToMap } from '@lib/geo/helpers';
import { IInnerLayer, MapStoreService } from '@lib/geo/map';
import { FsLvtAdvancedMarker } from '@lib/lvt';
import { SnLayoutService } from '@lib/ui/layout';

import { FsLvtCamera } from '../models/lvt-camera';
import { FsLvtIconLayerConfig } from '../models/lvt-icon-layer-config';
import { SnLvtMarker } from '../models/lvt-marker';
import { SnLvtService } from './lvt.service';

@Injectable()
export class MapLvtService implements OnDestroy, IInnerLayer {
  get hovered$(): Observable<FsLvtCamera> {
    return this._hovered$.asObservable();
  }

  get cameraSelected$(): Observable<FsLvtCamera> {
    return this._cameraSelected$.asObservable();
  }

  public readonly isAvailable: boolean;
  public readonly canChange: boolean;
  public advancedMarkers: FsLvtAdvancedMarker[] = [];
  public camerasParam: string;
  public pendingCameras = [];
  public activeLayerPopouts = [];
  public cameraLayerObjects = [];
  public followMarker;

  private readonly _hovered$ = new Subject<FsLvtCamera>();

  private readonly _cameraSelected$ = new Subject<FsLvtCamera>();
  private readonly _cancelDocking$ = new Subject<void>();

  private _map: GoogleMap;
  private readonly _mapLayer: GoogleMapLayer;
  private readonly _destroy$ = new Subject<void>();

  private _polyline: google.maps.Polyline;
  private _polylines: google.maps.Polyline[] = [];
  private _camera: FsLvtCamera;
  private _element: Element;
  private _deckGlOverlay: DeckOverlay;

  constructor(
    public readonly mapStoreService: MapStoreService,
    ability: SnAbilityService,
    private readonly _lvtService: SnLvtService,
    private readonly _layoutService: SnLayoutService,
    private readonly _sessionService: SnSessionService,
    private readonly _datadogService: FsDataDogRumService,
  ) {
    this._mapLayer = new GoogleMapLayer();
    this._deckGlOverlay = new DeckOverlay({
      id: 'map-lvt-layer',
      style: {
        zIndex: 1000,
      },
      glOptions: {
        preserveDrawingBuffer: true,
      },
      getTooltip: this._getTooltip.bind(this),
    });

    this.isAvailable = ability.can({ subject: 'cameras/camera', action: 'view' });
    this.canChange = ability.can({ subject: 'cameras/cameragroup', action: 'change' });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onMapLoaded(googleMap: GoogleMap): void {
    this._map = googleMap;
    if (!this.isAvailable) {
      return;
    }
    this._initLayer();

    this._map.on('styleChange').subscribe(() => {
      if (this._sessionService.webGlEnabled && this._deckGlOverlay) {
        this._deckGlOverlay.setMap(null);
        this._deckGlOverlay.finalize();
        this._deckGlOverlay = new DeckOverlay({
          id: 'map-lvt-layer',
          style: {
            zIndex: 1000,
          },
          glOptions: {
            preserveDrawingBuffer: true,
          },
          getTooltip: this._getTooltip.bind(this),
        });
      }
    });

    this._layoutService.appbar.modeChange$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this._onModeChange();
    });
  }

  public addToMap(): void {
    if (this._sessionService.webGlEnabled) {
      this._deckGlOverlay = new DeckOverlay({
        id: 'map-lvt-layer',
        style: {
          zIndex: 1000,
        },
        glOptions: {
          preserveDrawingBuffer: true,
        },
      });
    }
    this._mapLayer.addTo(this._map);
  }

  public removeFromMap(): void {
    if (this._sessionService.webGlEnabled && this._deckGlOverlay) {
      this._deckGlOverlay.finalize();
    }
    this._mapLayer.remove();
    this._clearAdvancedMarkers();
  }

  public addPolyline(camera: FsLvtCamera, element: Element): void {
    this.removePolyline();
    this._camera = camera;
    this._element = element;
    this._drawPolyline();
  }

  public addPolylines(cameras: FsLvtCamera[]): void {
    this.clearPolylines();
    cameras.forEach((camera) => {
      const element: Element = document.getElementById('lvt-camera-' + camera.id);
      if (element && camera.coords) {
        const dockCoords = this._getDockedElementCoords(element);
        const polylinePath = [dockCoords, camera.coords];
        const polyline = new google.maps.Polyline({
          path: polylinePath,
          geodesic: false,
          strokeColor: '#FFF',
          strokeOpacity: 1.0,
          strokeWeight: 2,
        });
        polyline.setMap(this._map.mapInstance);
        this._polylines.push(polyline);
      }
    });
  }

  public removePolyline(): void {
    this._camera = undefined;
    this._element = undefined;
    this._clearPolyline();
  }

  public clearPolylines(): void {
    this._polylines.forEach((p) => p.setMap(null));
    this._polylines = [];
  }

  public resetMapOptions(): void {
    if (!this._map) {
      return;
    }
    this._map.isDrawing = false;
    this._map.mapInstance.setOptions({
      draggable: true,
      scrollwheel: true,
      disableDoubleClickZoom: false,
      draggableCursor: '',
    });
  }

  public removeLine(): void {
    this._hovered$.next(null);
    this.removePolyline();
    this.clearPolylines();
  }

  public cancelDocking(): void {
    this._cancelDocking$.next();
  }

  public cameraSelected(camera: FsLvtCamera): void {
    this._cameraSelected$.next(camera);
  }

  public cameraHovered(camera: FsLvtCamera): void {
    this._hovered$.next(camera);
  }

  private _initLayer(): void {
    this._mapLayer.setZIndex(10);

    this._mapLayer.on('add').pipe(takeUntil(this._destroy$)).subscribe(this._fetchData.bind(this));

    this._mapLayer
      .on('click')
      .pipe(takeUntil(this._destroy$))
      .subscribe(({ targetElement }) => {
        this._onCameraLeftClick(targetElement.camera);
      });

    this._mapLayer
      .on('rightclick')
      .pipe(takeUntil(this._destroy$))
      .subscribe(({ targetElement }) => {
        this._onCameraLeftClick(targetElement.camera);
      });

    this._mapLayer
      .on('mouseover')
      .pipe(takeUntil(this._destroy$))
      .subscribe(({ targetElement }) => {
        this._hovered$.next(targetElement.camera);
      });

    this._mapLayer
      .on('mouseout')
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.removeLine();
      });
  }

  private _fetchData(): void {
    this._lvtService
      .getLvtCameras()
      .pipe(takeUntil(this._destroy$), takeUntil(this._mapLayer.on('remove')))
      .subscribe(({ results }) => {
        if (!this._sessionService.webGlEnabled) {
          this._mapLayer.clearOverlays();
          this._clearAdvancedMarkers();
          this._createAdvancedMarkers(results);
        } else {
          this._mapLayer.clearOverlays();
          this._clearAdvancedMarkers();
          const config = new FsLvtIconLayerConfig(
            'lvt-results-layer',
            results,
            (info, event) => {
              event.rightButton
                ? this._onCameraLeftClick(info.object)
                : this._onCameraLeftClick(info.object);
            },
            (info, event) => {
              info.object ? this._hovered$.next(info.object) : this.removeLine();
            },
          );
          const resultsLayer = new IconLayer(config);
          this._deckGlOverlay.setProps({
            layers: [resultsLayer],
            glOptions: {
              preserveDrawingBuffer: true,
            },
            onHover: (info) => {
              this._map.setHoveredLayers('cameras', !!info.object);
            },
            getTooltip: this._getTooltip.bind(this),
          });
          this._deckGlOverlay.setMap(this._map.mapInstance);
        }
      });
  }

  private _createAdvancedMarkers(cameras: FsLvtCamera[]): void {
    cameras
      .map((camera) => new SnLvtMarker(camera))
      .forEach((marker) => {
        const advancedMarker = new FsLvtAdvancedMarker(marker.camera);
        const existingMarkerIndex = this.advancedMarkers.findIndex(
          (advMarker) => advMarker.camera?.id === marker.camera?.id,
        );
        if (existingMarkerIndex > -1) {
          this.advancedMarkers[existingMarkerIndex].camera = marker.camera;
          this.advancedMarkers[existingMarkerIndex].addTo(this._map);
        } else {
          this.advancedMarkers.push(advancedMarker);
          advancedMarker.addTo(this._map);
        }
        advancedMarker.getMarker().addListener('click', () => {
          this._onCameraLeftClick(marker.camera);
        });
        advancedMarker.getMarker().element.addEventListener('contextmenu', () => {
          this._onCameraLeftClick(advancedMarker.camera);
        });
        ['mouseenter'].forEach((event) => {
          advancedMarker.getMarker().element.addEventListener(event, () => {
            this._hovered$.next(marker.camera);
          });
        });
        ['blur', 'pointerleave'].forEach((event) => {
          advancedMarker.getMarker().element.addEventListener(event, () => {
            this.removeLine();
            this.clearPolylines();
          });
        });
      });
  }

  private _onCameraLeftClick(camera: FsLvtCamera): void {
    this._datadogService.addAction('clicked_on_camera', {
      id: camera.id,
      name: camera.name,
      location: camera.locationId,
      geometry: camera.coordinates,
    });
    this._cameraSelected$.next(camera);
  }

  private _drawPolyline(): void {
    this._clearPolyline();
    const dockCoords = this._getDockedElementCoords(this._element);
    if (!this._camera?.coordinates) {
      return;
    }
    const polylinePath = [
      dockCoords,
      { lng: this._camera.coordinates[0], lat: this._camera.coordinates[1] },
    ];
    this._polyline = new google.maps.Polyline({
      path: polylinePath,
      geodesic: false,
      strokeColor: '#FFF',
      strokeOpacity: 1.0,
      strokeWeight: 2,
    });
    this._polyline.setMap(this._map.mapInstance);
  }

  private _getDockedElementCoords(element: Element): { lng: number; lat: number } {
    const mainDiv = document.getElementsByClassName('sn-layout-content');
    const paddingTop =
      mainDiv && mainDiv.length
        ? parseInt(window.getComputedStyle(mainDiv[0], null).paddingTop, 10)
        : 0;

    const parentClasses = element.parentElement.classList;
    const side: 'top' | 'right' | 'bottom' | 'left' = parentClasses.contains('cam-feed__row--side')
      ? 'left'
      : 'bottom';

    const domRect = element.getBoundingClientRect();

    let x: number;
    let y: number;
    switch (side) {
      case 'bottom':
        x = (domRect.right - domRect.left) / 2 + domRect.left;
        y = domRect.bottom - paddingTop;
        break;
      case 'left':
        x = domRect.left;
        y = (domRect.bottom - domRect.top) / 2 + domRect.top - paddingTop;
        break;
      default:
        x = (domRect.right - domRect.left) / 2 + domRect.left;
        y = domRect.bottom - paddingTop;
        break;
    }
    const dockLatLng = new FsPointToMap({ map: this._map, x, y }).coords;
    return { lng: dockLatLng.lng, lat: dockLatLng.lat };
  }

  private _clearPolyline(): void {
    if (this._polyline) {
      this._polyline.setMap(null);
    }
    this._polyline = undefined;
  }

  private _onModeChange(): void {
    if (this._polyline) {
      this._drawPolyline();
    }
  }

  private _clearAdvancedMarkers(): void {
    this.advancedMarkers.forEach((marker) => marker.remove());
    this.advancedMarkers = [];
  }

  private _getTooltip(info: any): any {
    return info.object && info.object.liveUnitName;
  }
}
